"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { ErrorContent } from "@/components/ErrorContent";

/**
 * The error boundary for everything under `[locale]`.
 *
 * Replaces the legacy `Error` component, which inspected `graphQLErrors` to pick
 * between 403, 404 and 500 — a shape that no longer exists. Backend failures now
 * surface as `ApiError` through react-query, which reports them as *state* rather
 * than throwing, so this boundary catches render crashes rather than fetch
 * failures. Data-loading errors belong next to the thing that was loading.
 *
 * `reset` re-renders the segment, which is worth offering: most crashes here are
 * transient (a style that failed to load, a WebGL context loss).
 */
export default function LocaleError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("error");

  useEffect(() => {
    // The digest is the only handle on the server-side stack, which Next withholds
    // from the client in production.
    console.error("[error boundary]", error.digest ?? "", error);
  }, [error]);

  return (
    <ErrorContent
      title={t("unexpected.title")}
      message={t("unexpected.message")}
      onRetry={reset}
    />
  );
}
